import { Controller } from "@hotwired/stimulus"

// loop-cap
// ---------------------------------------------------------------
// The page gets ten seconds of looping CSS animation, then it goes still.
// A dashboard left open used to pay for its mascots for as long as it was on
// screen (the energy argument is in in_view_animation_controller); now it
// goes quiet on its own and the screen feels lighter. Coming back — a new
// visit, a Turbo restore, the app returning from the background — starts a
// fresh ten seconds.
//
// One deadline for the whole page, not one per loop: a budget panel is
// display:none until it is swiped in, and its loops start fresh at that
// moment. After the deadline they are stopped as they start, so switching
// budgets a minute later does not wake the mascots up again.
//
// Finishing, not pausing: at the deadline each loop's iteration count is set
// to the cycle in progress (Web Animations API), so a bobbing mascot lands
// where it started and never freezes mid-air; a loop that starts after the
// deadline gets zero iterations and never moves. `alternate` loops round up
// to an even count so they end where they began. A finished CSS animation
// costs nothing; a paused one still holds its layer.
//
// Loops that mean "busy" are left alone: by animation name (spinners, the
// submitting button, voice listening) or under a data-loop-keep ancestor.
//
// Usage: <div data-controller="loop-cap">…</div>
//        <div data-controller="loop-cap" data-loop-cap-seconds-value="6">

const KEEP = new Set([
  "dashSyncSpin", "plaidSpin", "plaidOrbit", "plaidSheen", "evs-spin",
  "btn-submitting", "load-more-bar",
  "voiceWavePulse", "mascotListen",
  "otp-verify-wave", "otp-verify-fade"
])

export default class extends Controller {
  static values = { seconds: { type: Number, default: 10 } }

  connect() {
    this.capped = new Set()
    this.sweepQueued = false
    this.onStart = () => this.#queueSweep()
    this.onVisibility = () => { if (document.visibilityState === "visible") this.#restart() }
    this.element.addEventListener("animationstart", this.onStart, true)
    document.addEventListener("visibilitychange", this.onVisibility)
    this.#arm()
  }

  disconnect() {
    this.element.removeEventListener("animationstart", this.onStart, true)
    document.removeEventListener("visibilitychange", this.onVisibility)
    clearTimeout(this.timer)
    this.capped.clear()
  }

  // ── The deadline ──────────────────────────────────────────────────
  #arm() {
    clearTimeout(this.timer)
    this.past = false
    this.timer = setTimeout(() => {
      this.past = true
      this.#capAll(false)
    }, this.secondsValue * 1000)
  }

  // A loop that starts once the deadline has passed (a budget panel swiped
  // in, a popup opening) is stopped before its first frame. animationstart
  // can fire in bursts (a tab switch re-runs every bar segment); one sweep
  // per frame is plenty.
  #queueSweep() {
    if (!this.past || this.sweepQueued) return
    this.sweepQueued = true
    requestAnimationFrame(() => {
      this.sweepQueued = false
      this.#capAll(true)
    })
  }

  // ── Capping ───────────────────────────────────────────────────────
  #capAll(fresh) {
    this.#loops().forEach((anim) => this.#cap(anim, fresh))
  }

  #cap(anim, fresh) {
    const timing = anim.effect?.getTiming()
    if (!timing || timing.iterations !== Infinity) return
    const duration = Number(timing.duration) || 0
    if (duration <= 0) return

    let iterations = 0
    if (!fresh) {
      const elapsed = Math.max(0, (anim.currentTime ?? 0) - (timing.delay || 0))
      iterations = Math.max(1, Math.ceil(elapsed / duration))
      if (String(timing.direction).startsWith("alternate") && iterations % 2 === 1) iterations += 1
    }

    anim.effect.updateTiming({ iterations })
    this.capped.add(anim)
  }

  // Back from the background: every capped loop picks up exactly where it
  // would have been (a finished animation's clock keeps running), and the
  // ten seconds start over for the whole page.
  #restart() {
    this.capped.forEach((anim) => {
      try { anim.effect.updateTiming({ iterations: Infinity }) } catch { /* animation gone */ }
    })
    this.capped.clear()
    this.#arm()
  }

  // ── Helpers ───────────────────────────────────────────────────────
  // document.getAnimations(), not element.getAnimations({ subtree }): every
  // Safari answers the document call, and it lists pseudo-element loops
  // (::before sparkles) alongside the rest. Filtered down to this host.
  #loops() {
    return document.getAnimations().filter((anim) => this.#isLoop(anim))
  }

  #isLoop(anim) {
    const target = anim.effect?.target
    if (!target || !this.element.contains(target)) return false
    if (anim.effect.getTiming().iterations !== Infinity) return false
    if (KEEP.has(anim.animationName)) return false
    return !target.closest?.("[data-loop-keep]")
  }
}
